import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Mail,
  Eye,
  UserPlus,
  Plus,
  Activity,
  TrendingUp,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  CHART_COLORS,
  DATE_PRESETS,
  opensByDay,
  uniqueOpeners,
  withinDateRange,
} from "../lib/analytics";

const API_BASE_URL = "https://email-backend.heybuddy.co.in";

const chartTooltip = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "0.5rem",
  color: "hsl(var(--card-foreground))",
  fontSize: "0.8rem",
};

const nf = (n) => (n ?? 0).toLocaleString();

const StatCard = ({ title, value, icon: Icon, hint, loading }) => (
  <Card className="animate-fadeIn">
    <CardContent className="flex items-start justify-between p-5">
      <div className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <p className="text-2xl font-bold tracking-tight">{value}</p>
        )}
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
        <Icon className="h-5 w-5" />
      </div>
    </CardContent>
  </Card>
);

const AddAccountForm = ({ onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/emails/add`, {
        name,
        email,
        password,
      });
      if (response.status === 201) {
        toast.success("Account added successfully!");
        onClose();
      } else throw new Error("Failed to add account");
    } catch (err) {
      const msg = err.response?.data?.message || "Error adding account";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {[
        { id: "name", label: "Name", value: name, set: setName, type: "text" },
        { id: "email", label: "Email", value: email, set: setEmail, type: "email" },
        { id: "password", label: "Password", value: password, set: setPassword, type: "password" },
      ].map((f) => (
        <div key={f.id} className="space-y-1.5">
          <label htmlFor={f.id} className="text-sm font-medium">{f.label}</label>
          <Input id={f.id} type={f.type} value={f.value} onChange={(e) => f.set(e.target.value)} required />
        </div>
      ))}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting ? "Adding..." : "Add Account"}
      </Button>
    </form>
  );
};

const HomePage = () => {
  const [reports, setReports] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);

  const [category, setCategory] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [rep, camp] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/reports`).catch(() => ({ data: [] })),
          axios.get(`${API_BASE_URL}/api/campaigns`).catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        setReports(Array.isArray(rep.data) ? rep.data : []);
        setCampaigns(Array.isArray(camp.data) ? camp.data : []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const categories = useMemo(
    () =>
      [...new Set(campaigns.map((c) => (c.category || "").trim()).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [campaigns]
  );

  const filteredReports = useMemo(() => {
    return reports.filter((r) => {
      const c = r?.campaignId;
      if (!c) return category === "all" && datePreset === "all";
      if (category !== "all" && (c.category || "").trim() !== category) return false;
      if (!withinDateRange(c.createdAt, datePreset, customFrom, customTo)) return false;
      return true;
    });
  }, [reports, category, datePreset, customFrom, customTo]);

  const analytics = useMemo(() => {
    const allDetails = [];
    let totalOpens = 0;
    const perCampaign = [];
    const byCategory = {};

    filteredReports.forEach((r) => {
      const details = r?.opens?.details || [];
      totalOpens += r?.opens?.total || details.length;
      if (details.length) allDetails.push(...details);
      const name = r?.campaignId?.name || "Untitled";
      const cat = (r?.campaignId?.category || "Uncategorized").trim() || "Uncategorized";
      perCampaign.push({ name, opens: r?.opens?.total || details.length });
      byCategory[cat] = (byCategory[cat] || 0) + (r?.opens?.total || details.length);
    });

    const timeSeries = opensByDay(allDetails);
    const topCampaigns = [...perCampaign]
      .sort((a, b) => b.opens - a.opens)
      .slice(0, 8)
      .map((c) => ({ ...c, short: c.name.length > 22 ? c.name.slice(0, 21) + "…" : c.name }));
    const categoryData = Object.entries(byCategory)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));

    return {
      totalOpens,
      unique: uniqueOpeners(allDetails),
      campaignCount: filteredReports.length,
      avgOpens: filteredReports.length
        ? Math.round(totalOpens / filteredReports.length)
        : 0,
      timeSeries,
      topCampaigns,
      categoryData,
    };
  }, [filteredReports]);

  const activeFilters = (category !== "all" ? 1 : 0) + (datePreset !== "all" ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fadeIn flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Analytics Overview</h2>
          <p className="text-muted-foreground">
            Engagement across {nf(campaigns.length)} campaigns — measured by email opens.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isAddAccountOpen} onOpenChange={setIsAddAccountOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserPlus className="mr-2 h-4 w-4" /> Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Add New Account</DialogTitle>
              </DialogHeader>
              <AddAccountForm onClose={() => setIsAddAccountOpen(false)} />
            </DialogContent>
          </Dialog>
          <Button onClick={() => navigate("/campaigns")}>
            <Plus className="mr-2 h-4 w-4" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="animate-fadeIn">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <span className="text-sm font-medium text-muted-foreground">Filters</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {DATE_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {datePreset === "custom" && (
            <div className="flex items-center gap-2">
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-auto" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-auto" />
            </div>
          )}
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => {
                setCategory("all");
                setDatePreset("all");
                setCustomFrom("");
                setCustomTo("");
              }}
            >
              <X className="mr-1 h-3.5 w-3.5" /> Clear
            </Button>
          )}
          <span className="ml-auto text-sm text-muted-foreground">
            {nf(analytics.campaignCount)} campaigns in view
          </span>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Campaigns" value={nf(analytics.campaignCount)} icon={Mail} loading={loading} />
        <StatCard title="Total Opens" value={nf(analytics.totalOpens)} icon={Eye} loading={loading} />
        <StatCard title="Unique Openers" value={nf(analytics.unique)} icon={Users} loading={loading} />
        <StatCard title="Avg Opens / Campaign" value={nf(analytics.avgOpens)} icon={Activity} loading={loading} />
      </div>

      {/* Opens over time */}
      <Card className="animate-fadeIn">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Opens Over Time</CardTitle>
            <p className="text-sm text-muted-foreground">Daily email opens across selected campaigns</p>
          </div>
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            {loading ? (
              <Skeleton className="h-full w-full" />
            ) : analytics.timeSeries.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.timeSeries} margin={{ left: -18, right: 8, top: 4 }}>
                  <defs>
                    <linearGradient id="opensGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={chartTooltip} />
                  <Area type="monotone" dataKey="opens" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#opensGrad)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Top campaigns + category split */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="animate-fadeIn lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-lg">Top Campaigns by Opens</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full min-w-0">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : analytics.topCampaigns.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={analytics.topCampaigns}
                    layout="vertical"
                    margin={{ left: 8, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="short" width={150} stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={chartTooltip} cursor={{ fill: "hsl(var(--muted) / 0.4)" }} />
                    <Bar dataKey="opens" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} isAnimationActive={false} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fadeIn lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Opens by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 w-full min-w-0">
              {loading ? (
                <Skeleton className="h-full w-full" />
              ) : analytics.categoryData.length === 0 ? (
                <EmptyChart />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {analytics.categoryData.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={chartTooltip} />
                    <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Note: click &amp; delivery tracking isn't recorded yet — analytics are based on email opens.
      </p>
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
    <Activity className="mb-2 h-8 w-8 opacity-40" />
    <p className="text-sm">No data for the selected filters</p>
  </div>
);

export default HomePage;
